import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ordersAPI, paymentAPI } from '../../utils/api';

export const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order');
  // Sadad appends invoice_Id (various casings) to the successURL after payment
  const invoiceId = searchParams.get('invoice_Id')
                 || searchParams.get('invoice_id')
                 || searchParams.get('invoiceId');
  const [order, setOrder] = useState(null);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  useEffect(() => {
    // If we came back from Sadad with an invoice_Id, ask the backend to verify + mark paid
    if (invoiceId && orderNumber) {
      paymentAPI.verify({ invoice_id: invoiceId, order_number: orderNumber })
        .catch(err => console.warn('[success] verify failed (webhook will catch up):', err.message));
    }
    if (orderNumber) {
      // Small delay so a verify() that flipped status has time to write before we read
      const delay = invoiceId ? 800 : 0;
      const t = setTimeout(() => {
        ordersAPI.getByNumber(orderNumber).then(setOrder).catch(() => {});
      }, delay);
      return () => clearTimeout(t);
    }
  }, [orderNumber, invoiceId]);

  const isCod = order?.payment_method === 'cod';
  const totalLabel = isCod ? t('success.totalDue') : t('success.totalPaid');

  return (
    <div className="success-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="success-card">
        <div className="success-icon">✅</div>
        <h1>{t('success.title')}</h1>
        <p>{isCod ? t('success.codMessage') : t('success.message')}</p>
        {orderNumber && <p className="success-order-no">{t('success.orderNo', { number: orderNumber })}</p>}
        {order && (
          <div className="success-details">
            <div><span>{totalLabel}</span><strong>KD {parseFloat(order.total).toFixed(3)}</strong></div>
            <div><span>{t('success.deliveryTo')}</span><strong>{order.shipping_governorate}</strong></div>
            <div><span>{t('success.status')}</span><strong style={{ textTransform: 'capitalize' }}>{order.status}</strong></div>
          </div>
        )}
        <div className="success-actions">
          <Link to="/shop" className="btn btn-primary">{t('success.continueShopping')}</Link>
          <Link to="/account/orders" className="btn btn-outline">{t('success.trackOrder')}</Link>
        </div>
      </div>

      <style>{`
        .success-page { min-height: 70vh; display: flex; align-items: center; justify-content: center; padding: 40px 20px; }
        .success-card { background: white; border-radius: var(--radius-lg); padding: 60px 40px; text-align: center; max-width: 480px; width: 100%; box-shadow: var(--shadow-lg); }
        .success-icon { font-size: 4rem; margin-bottom: 20px; }
        .success-card h1 { font-family: var(--font-serif); margin-bottom: 12px; color: var(--olive); }
        .success-card > p { color: var(--text-muted); margin-bottom: 8px; }
        .success-order-no { font-weight: 700; font-size: 1.1rem; color: var(--gold); margin: 16px 0; }
        .success-details { background: var(--cream); border-radius: var(--radius); padding: 20px; margin: 20px 0; display: flex; flex-direction: column; gap: 10px; text-align: left; }
        .success-details div { display: flex; justify-content: space-between; font-size: 0.9rem; }
        .success-details span { color: var(--text-muted); }
        .success-actions { display: flex; gap: 12px; justify-content: center; margin-top: 24px; flex-wrap: wrap; }
      `}</style>
    </div>
  );
};

export const OrderCancelPage = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, textAlign: 'center', padding: 40 }}>
    <span style={{ fontSize: '3rem' }}>😔</span>
    <h2 style={{ fontFamily: 'var(--font-serif)' }}>Payment Cancelled</h2>
    <p style={{ color: 'var(--text-muted)' }}>Your order has not been processed. You can try again.</p>
    <Link to="/checkout" className="btn btn-primary">Try Again</Link>
    <Link to="/shop" className="btn btn-outline">Continue Shopping</Link>
  </div>
);
